import axios from 'axios'

const jsonHeaders = { 'Content-Type': 'application/json' }

function requestConfig() {
  return { withCredentials: true, headers: jsonHeaders }
}

/** @typedef {'course' | 'instructor' | 'feature' | 'taxExempt' | 'billingAddress' | 'billingDocumentExtension'} PortalRequestNotificationType */

/**
 * RefReps has responded to this request (approved, denied, or cancelled).
 * @param {Record<string, unknown> | null | undefined} row
 */
export function isRespondedPortalRequest(row) {
  if (!row) return false
  const status = String(row.status ?? 'pending').toLowerCase()
  if (status === 'pending') return false
  return !!(row.responseDate || row.respondedAt)
}

/**
 * Responded request the current org user has not marked viewed.
 * @param {Record<string, unknown> | null | undefined} row
 */
export function isUnreadPortalRequest(row) {
  return isRespondedPortalRequest(row) && row.hasViewed !== true
}

/**
 * Tax exempt cert has a non-pending status response the user has not viewed.
 * @param {Record<string, unknown> | null | undefined} cert
 */
export function isUnreadTaxExemptCert(cert) {
  if (!cert) return false
  const status = String(cert.status ?? 'pending').toLowerCase()
  if (status === 'pending') return false
  return cert.hasViewed !== true
}

/**
 * @param {string} certStatus
 */
export function taxExemptStatusToNotifStatus(certStatus) {
  const key = String(certStatus ?? '').toLowerCase()
  if (key === 'active' || key === 'approved') return 'approved'
  if (key === 'denied') return 'denied'
  return 'cancelled'
}

/**
 * @param {Record<string, unknown>} row
 */
export function readHasViewedFromApi(row) {
  if (row.hasViewed === true || row.viewed === true) return true
  if (row.viewedAt || row.responseViewedAt) return true
  return false
}

/**
 * @param {PortalRequestNotificationType} type
 * @param {string | number} requestId
 */
export function portalNotificationItem(type, requestId) {
  return { type, requestId: String(requestId) }
}

/**
 * Mark portal request notifications as read for the current user + organization.
 * Backend: POST /mark-portal-request-notifications-viewed
 *
 * @param {{
 *   organizationId: string,
 *   items?: Array<{ type: PortalRequestNotificationType, requestId: string }>,
 *   markAll?: boolean,
 * }} payload
 */
export async function postMarkPortalRequestNotificationsViewed(payload) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/mark-portal-request-notifications-viewed`,
    payload,
    requestConfig()
  )
  return data
}

/**
 * @param {Array<Record<string, unknown>>} rows
 * @param {Set<string> | string[]} viewedIds
 */
export function applyHasViewedToRequestRows(rows, viewedIds) {
  const idSet = new Set(
    (viewedIds instanceof Set ? [...viewedIds] : viewedIds).map(String)
  )
  return rows.map((row) =>
    idSet.has(String(row.id)) ? { ...row, hasViewed: true } : row
  )
}

/**
 * @param {Array<Record<string, unknown>>} rows
 */
export function applyMarkAllViewedToTaxExemptCerts(rows) {
  return rows.map((row) => {
    const status = String(row.status ?? 'pending').toLowerCase()
    if (status === 'pending') return row
    return { ...row, hasViewed: true }
  })
}

/**
 * @param {Array<Record<string, unknown>>} rows
 */
export function applyMarkAllViewedToRequestRows(rows) {
  return rows.map((row) =>
    isRespondedPortalRequest(row) ? { ...row, hasViewed: true } : row
  )
}
